import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, Plus } from "lucide-react";

export default function AITutorChat({ persona = "tutor", title = "AI Tutor" }) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const loadSessions = async () => {
    try { const { data } = await api.get("/ai/chat/sessions"); setSessions(data.sessions || []); } catch {}
  };

  const loadHistory = async (sid) => {
    try { const { data } = await api.get("/ai/chat/history", { params: { session_id: sid } }); setMessages(data.messages || []); }
    catch { setMessages([]); }
  };

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const newSession = () => { setSessionId(""); setMessages([]); };
  const pick = async (sid) => { setSessionId(sid); await loadHistory(sid); };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;
    const userText = input;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: userText }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: userText, session_id: sessionId, persona });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI error");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, something went wrong." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="grid lg:grid-cols-4 gap-4 h-[calc(100vh-7rem)]" data-testid="ai-chat-page">
      <aside className="hidden lg:block bg-white border border-neutral-200 overflow-y-auto">
        <div className="p-4 border-b border-neutral-100 flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">Sessions</div>
          <button onClick={newSession} className="text-xs inline-flex items-center gap-1 hover:text-[#0A0C10] text-neutral-500"><Plus size={12} /> New</button>
        </div>
        {sessions.length === 0 ? (
          <div className="p-4 text-xs text-neutral-500">No sessions yet.</div>
        ) : sessions.map((s) => (
          <button key={s.session_id} onClick={() => pick(s.session_id)} className={`w-full text-left p-3 text-xs hover:bg-neutral-50 border-b border-neutral-100 ${sessionId === s.session_id ? "bg-neutral-50" : ""}`}>
            <div className="font-medium truncate text-neutral-700">{s.preview || "Conversation"}</div>
            <div className="text-[10px] text-neutral-400 mt-1">{s.last?.slice(0, 16).replace("T", " ")}</div>
          </button>
        ))}
      </aside>

      <section className="lg:col-span-3 bg-white border border-neutral-200 flex flex-col">
        <div className="p-4 border-b border-neutral-200">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ {persona}</div>
          <h1 className="font-display font-bold text-2xl tracking-tighter inline-flex items-center gap-2">
            <Sparkles size={18} className="text-[#FFD600]" /> {title}
          </h1>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !loading && (
            <div className="text-center text-sm text-neutral-500 py-12">
              {persona === "tutor"
                ? "Ask anything — homework help, topic explanations, exam prep."
                : "Ask your AI teaching assistant — engagement tips, lesson ideas, English coaching."}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0A0C10] text-white" : "bg-neutral-100 text-neutral-800"}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-neutral-100 px-4 py-3 text-sm text-neutral-500">AI is thinking…</div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="p-3 border-t border-neutral-200 flex gap-2">
          <input
            data-testid="ai-chat-input"
            value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 bg-white border border-neutral-300 px-4 py-3 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
          />
          <button type="submit" data-testid="ai-chat-send" disabled={loading} className="bg-[#FFD600] text-[#0A0C10] font-bold px-5 inline-flex items-center gap-2 disabled:opacity-60">
            <Send size={14} /> Send
          </button>
        </form>
      </section>
    </div>
  );
}
